import React, { memo, useState, useEffect, useMemo, Fragment, useCallback, useRef } from 'react';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';

import { Image, Spinner } from 'components';
import { Radio, Input, Checkbox, SelectionCheckbox } from 'components/form';
import IconButton from 'components/IconButton';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { Option } from 'lib/models/option';

import styles from './OptionSelect.module.scss';

export type OptionSelectCommonProps = {
  className?: string;
  options?: Array<Option>;
  isSearchable?: boolean;
  placeholder?: string;
  onFetchMoreData?: () => void;
  onSearch?: (searchText: string) => void;
  showRefLoader?: boolean;
};

type SingleSelectProps = OptionSelectCommonProps & {
  value: string;
  onChange: (value: string) => void;
};

type MultiSelectProps = OptionSelectCommonProps & {
  values: Array<string>;
  onChange: (values: Array<string>) => void;
  chipOption?: boolean;
};

type GroupedMultiSelectProps = OptionSelectCommonProps & {
  values: Array<string>;
  subValues: Array<string>;
  subOptions: Array<Option>;
  onChange: ({ values, subValues }: { values: Array<string>; subValues: Array<string> }) => void;
};

type OptionSelectProps = SingleSelectProps | MultiSelectProps | GroupedMultiSelectProps;

const isSingleSelect = (props: OptionSelectProps): props is SingleSelectProps => Object.prototype.hasOwnProperty.call(props, 'value');
const isMultiSelect = (props: OptionSelectProps): props is MultiSelectProps =>
  Object.prototype.hasOwnProperty.call(props, 'values') && !Object.prototype.hasOwnProperty.call(props, 'subOptions');
const isGroupedMultiSelect = (props: OptionSelectProps): props is GroupedMultiSelectProps =>
  Object.prototype.hasOwnProperty.call(props, 'values') &&
  Object.prototype.hasOwnProperty.call(props, 'subValues') &&
  Object.prototype.hasOwnProperty.call(props, 'subOptions');

const OptionSelect: React.FC<OptionSelectProps> = memo((props) => {
  const [tc] = useTranslation('common');
  const { className = '', options, isSearchable = true, placeholder, onFetchMoreData, onSearch, showRefLoader } = props;

  const contentClassNames = cx(styles.container, className);
  const [search, setSearch] = useState('');
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null);
  const trimmedSearch = search.trim();
  const bottomListRef = useRef<HTMLDivElement | null>(null);

  useVisibleOnIntersect({
    elRef: bottomListRef,
    threshold: 0,
    onIntersect: onFetchMoreData,
    rootMargin: '50px',
  });

  useEffect(() => {
    return () => {
      if (isSearchable) setSearch('');
    };
  }, [isSearchable]);

  useEffect(() => {
    onSearch && onSearch(search);
  }, [search]);

  const searchString = trimmedSearch.toLowerCase();

  const filteredSubOptions = useMemo(() => {
    if (!isGroupedMultiSelect(props)) return [];

    return isSearchable && searchString && !onSearch
      ? props.subOptions.filter((option) => option.label.toLowerCase().indexOf(searchString) >= 0)
      : props.subOptions;
  }, [isSearchable, searchString, props]);

  const filteredOptions = useMemo(() => {
    if (!options) return [];

    if (isSearchable && searchString && !onSearch) {
      return options.filter(
        (option) =>
          option.label.toLowerCase().indexOf(searchString) >= 0 || filteredSubOptions.some((subOption) => subOption.parent_id === option.value)
      );
    } else {
      return options;
    }
  }, [isSearchable, searchString, options, filteredSubOptions]);

  const handleValuesChange = (value: string) => {
    if (isSingleSelect(props)) {
      props.onChange(value);
    } else if (isMultiSelect(props)) {
      const newValues = props.values.includes(value) ? props.values.filter((val) => val !== value) : [...props.values, value];
      props.onChange(newValues);
    } else if (isGroupedMultiSelect(props)) {
      const newValues = props.values.includes(value) ? props.values.filter((val) => val !== value) : [...props.values, value];
      props.onChange({ values: newValues, subValues: props.subValues });
    }
  };

  const onSubValuesChange = (subValue: string, parent_id: string) => {
    if (!isGroupedMultiSelect(props)) return;

    const { values, subValues, subOptions, onChange } = props;

    let newValues = values;
    let newSubValues = subValues.includes(subValue) ? subValues.filter((val) => val !== subValue) : [...subValues, subValue];
    if (newSubValues.length > subValues.length) {
      const itemSubOptions = subOptions.filter((subOption) => subOption.parent_id === parent_id);

      // When all subOptions are selected, select the parent option and remove the all related subOption valaues from subValues
      const isAllSubValuesSelected = itemSubOptions.every((subOption) => newSubValues.includes(subOption.value));
      newValues = isAllSubValuesSelected ? [...values, parent_id] : values;
      newSubValues = isAllSubValuesSelected
        ? newSubValues.filter((selectedSubValue) => !itemSubOptions.some((itemSubOption) => itemSubOption.value === selectedSubValue))
        : newSubValues;
    }
    onChange({ values: newValues, subValues: newSubValues });
  };

  const onDeselectAllChildren = (parentValue: string) => {
    if (!isGroupedMultiSelect(props)) return;
    const { subOptions, subValues, values, onChange } = props;

    const subValuesToRemove = subOptions.filter((subOption) => subOption.parent_id === parentValue).map((subOption) => subOption.value);
    const newSubValues = subValues.filter((subValue) => !subValuesToRemove.includes(subValue));
    onChange({ values: values, subValues: newSubValues });
  };

  const handleDeselectChild = (parentValue: string, childValue: string) => {
    if (!isGroupedMultiSelect(props)) return;
    const { subOptions, values, onChange } = props;

    const parentChildrenValues = subOptions
      .filter((subOption) => subOption.parent_id === parentValue && subOption.value !== childValue)
      .map((subOption) => subOption.value);
    const newValues = values.filter((value) => value !== parentValue);
    onChange({ values: newValues, subValues: parentChildrenValues });
  };

  const selectedSubOptions = useMemo(() => {
    if (!expandedOptionId) {
      return [];
    }

    return filteredSubOptions.filter((option) => option.parent_id === expandedOptionId);
  }, [expandedOptionId, filteredSubOptions]);

  const onToggleSubOptions = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>, optionId: string) => {
    event.stopPropagation();
    setExpandedOptionId((prevId) => (prevId === optionId ? null : optionId));
  }, []);

  const isMultiSelectChip = isMultiSelect(props) && props.chipOption;

  return (
    <div className={contentClassNames} data-testid="OptionSelect">
      {isSearchable ? (
        <div className={styles.search}>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className={styles.input}
            startIconClassName={styles['search-icon']}
            placeholder={placeholder}
            startIcon="search"
            startIconSize="small"
            endIconSize="xsmall"
            endIcon={search ? 'cancel' : undefined}
            onEndIconClick={() => setSearch('')}
            autoFocus={true}
          />
        </div>
      ) : null}
      {typeof options === 'undefined' && (
        <div className={styles.spinner}>
          <Spinner size="small" />
        </div>
      )}
      {typeof options !== 'undefined' && filteredOptions.length === 0 && <p className={styles['no-options']}>{tc('no-search-options')}</p>}
      {filteredOptions.length > 0 && (
        <ul className={cx(styles.options, { [styles['chip-options']]: isMultiSelectChip })}>
          {filteredOptions?.map((option, index) => {
            const { id, label, value } = option;
            let SelectionComponent,
              isChecked = false,
              isHalfChecked = false,
              areAllChildrenSelected = false,
              isSomeChildrenSelected = false;

            if (isGroupedMultiSelect(props)) {
              SelectionComponent = Checkbox;
              const { values, subValues, subOptions } = props;
              const checked = values.includes(value);
              const itemSubOptions = subOptions.filter((subOption) => subOption.parent_id === value);
              areAllChildrenSelected = itemSubOptions.length > 0 && itemSubOptions.every((subOption) => subValues.includes(subOption.value));
              isSomeChildrenSelected = itemSubOptions.length > 0 && itemSubOptions.some((subOption) => subValues.includes(subOption.value));
              isChecked = checked || areAllChildrenSelected;
              isHalfChecked = isSomeChildrenSelected && !areAllChildrenSelected;
            } else if (isMultiSelect(props)) {
              SelectionComponent = props.chipOption ? SelectionCheckbox : Checkbox;
              isChecked = props.values.includes(option.value);
            } else {
              SelectionComponent = Radio;
              isChecked = props.value === option.value;
            }

            return (
              <Fragment key={id}>
                <li
                  key={id}
                  className={styles.option}
                  onClick={() => (areAllChildrenSelected || isSomeChildrenSelected ? onDeselectAllChildren(value) : handleValuesChange(value))}
                >
                  {isGroupedMultiSelect(props) && (
                    <IconButton
                      iconName="icon_arrow-down"
                      size="xsmall"
                      onClick={(event) => onToggleSubOptions(event, id)}
                      className={cx(styles['option-icon'], {
                        [styles.expanded]: expandedOptionId === id,
                      })}
                    />
                  )}
                  <SelectionComponent
                    checked={isChecked}
                    halfChecked={isHalfChecked}
                    readOnly
                    labelFirst={true}
                    className={styles['option-selection']}
                    labelClassName={styles['option-label']}
                    hasBorder
                  >
                    {label}
                  </SelectionComponent>
                </li>
                {expandedOptionId === id &&
                  isGroupedMultiSelect(props) &&
                  selectedSubOptions.map(({ id: childId, value, label, avatar, parent_id }) => {
                    const checked = props.subValues.includes(value);
                    const parentChecked = parent_id ? props.values.includes(parent_id) : false;
                    const isChecked = checked || parentChecked;

                    return (
                      <>
                        <div className={styles['sub-divider']}></div>
                        <li
                          key={childId}
                          className={cx(styles.option, styles['sub-option'], {
                            [styles.selected]: isChecked,
                          })}
                          onClick={() =>
                            parent_id ? (parentChecked ? handleDeselectChild(parent_id, value) : onSubValuesChange(value, parent_id)) : null
                          }
                        >
                          <Checkbox checked={isChecked} labelFirst={true} readOnly className={styles['option-selection']}>
                            {avatar ? <Image src={avatar} alt={label} /> : null}
                            <span>{label}</span>
                          </Checkbox>
                        </li>
                      </>
                    );
                  })}
                {filteredOptions.length - 1 !== index && !isMultiSelectChip && <div className={styles.divider}></div>}
              </Fragment>
            );
          })}
          {onFetchMoreData && (
            <div className={styles.spinner} ref={bottomListRef}>
              {showRefLoader && <Spinner size="xxsmall" />}
            </div>
          )}
        </ul>
      )}
    </div>
  );
});

OptionSelect.displayName = 'OptionSelect';

export default OptionSelect;
